import {
  AppCard,
  Icon,
  InfoField,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiAccount,
  mdiCalendarBlank,
  mdiCheckCircleOutline,
  mdiClockOutline,
  mdiCloseCircleOutline,
  mdiFileDocumentOutline,
  mdiListBoxOutline,
} from "@mdi/js";
import { Alert, Box, Chip, Grid, Link, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { Attachment } from "../../../../@types/services/Attachment";
import { FileList } from "../../../../components/molecules/FileList";
import { UserInfoBadge } from "../../../../components/molecules/UserInfoBadge";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { useAuth } from "../../../../hooks/useAuth";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";
import { ApproveButtons } from "../../request_anticipation_evaluation_quotations/-internal_components/ApproveButtons";
import { requestAnticipationServiceQuotationDetailQueryOptions } from "../-queries";

const getStatusColor = (
  status: string,
): "error" | "warning" | "success" | "default" => {
  switch (status) {
    case "rejected":
      return "error";
    case "pending":
      return "warning";
    case "approved":
    case "automatically_closed":
      return "success";
    default:
      return "default";
  }
};

const DetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { requestAnticipationServiceQuotationId } = Route.useParams();

  const { data: requestAnticipationServiceQuotation, isLoading } = useQuery(
    requestAnticipationServiceQuotationDetailQueryOptions(
      requestAnticipationServiceQuotationId,
    ),
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t("loading")}</Typography>
      </Box>
    );
  }

  if (!requestAnticipationServiceQuotation) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t("noData")}</Typography>
      </Box>
    );
  }

  const {
    approver,
    created_at: createdAt,
    justification,
    reason,
    reason_files: reasonFiles,
    requested_by: requestedBy,
    service_quotation: serviceQuotation,
    service_quotation_id: serviceQuotationId,
    status,
    updated_at: updatedAt,
  } = requestAnticipationServiceQuotation;

  return (
    <PageContainer showBackButton title={serviceQuotation.reference_number}>
      {status === "pending" ? (
        <Alert severity="warning">
          {t(
            "requestAnticipationEvaluationQuotations.messages.requestPendingApproval",
          )}
        </Alert>
      ) : null}

      <Grid
        container
        spacing={3}
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 3,
          mt: 0,
          width: "100%",
        }}
      >
        <Grid
          size={8}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <AppCard
            icon={<Icon icon={mdiFileDocumentOutline} />}
            title={t(
              "requestAnticipationEvaluationQuotations.titles.requestDetails",
            )}
          >
            <Box
              sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr" }}
            >
              <InfoField label={t("common.requestedBy")}>
                <UserInfoBadge
                  avatarIcon={<Icon icon={mdiAccount} />}
                  avatarType="icon"
                  info={requestedBy.full_name}
                  photoUser={requestedBy}
                  subInfo={requestedBy.login}
                />
              </InfoField>

              <InfoField label={t("common.serviceQuotation")}>
                <Link
                  href={`/vbuyer/service_quotations/${serviceQuotationId}`}
                  rel="noreferrer"
                  target="_blank"
                  underline="hover"
                >
                  {serviceQuotation.reference_number}
                </Link>
              </InfoField>

              <InfoField
                label={t("common.purchaseRequisition")}
                text={serviceQuotation.pr_item.pr_number}
              />
            </Box>

            <InfoField
              label={t("common.reason")}
              text={reason}
              variant="boxed"
            />

            <FileList attachments={reasonFiles as Attachment[]} />
          </AppCard>

          {status === "pending" ? (
            <AppCard
              icon={<Icon color="warning" icon={mdiClockOutline} />}
              slotProps={{
                root: { sx: { borderColor: "warning.main" } },
                title: { sx: { color: "warning.main" } },
              }}
              title={t(
                "requestAnticipationEvaluationQuotations.titles.pendingApproval",
              )}
            >
              {user?.roles_names.includes(
                "Request Anticipation Service Quotation - Update",
              ) ? (
                <Box sx={{ mt: 2 }}>
                  <ApproveButtons
                    requestAnticipationEvaluationQuotationIds={[
                      String(requestAnticipationServiceQuotation.id),
                    ]}
                    serviceQuotation
                  />
                </Box>
              ) : null}
            </AppCard>
          ) : null}

          {status === "rejected" ? (
            <AppCard
              icon={<Icon color="error" icon={mdiCloseCircleOutline} />}
              slotProps={{
                root: { sx: { borderColor: "error.main" } },
                title: { sx: { color: "error.main" } },
              }}
              title={t(
                "requestAnticipationEvaluationQuotations.titles.rejected",
              )}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: "1fr 1fr",
                }}
              >
                <InfoField label={t("common.rejectedBy")}>
                  {approver ? (
                    <UserInfoBadge
                      avatarIcon={<Icon icon={mdiAccount} />}
                      avatarType="icon"
                      info={approver.full_name}
                      photoUser={approver}
                      subInfo={approver.login}
                    />
                  ) : null}
                </InfoField>
                <InfoField
                  label={t("common.rejectedAt")}
                  text={t("intlDateTime", i18nextParseToDate(updatedAt))}
                />
              </Box>
              <InfoField
                label={t("common.rejectionJustification")}
                text={justification}
                textColor="text.secondary"
                variant="boxed"
              />
            </AppCard>
          ) : null}

          {["approved", "automatically_closed"].includes(status) ? (
            <AppCard
              icon={<Icon color="success" icon={mdiCheckCircleOutline} />}
              slotProps={{
                root: { sx: { borderColor: "success.main" } },
                title: { sx: { color: "success.main" } },
              }}
              title={t(
                "requestAnticipationEvaluationQuotations.titles.approved",
              )}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: "1fr 1fr",
                }}
              >
                <InfoField label={t("common.approvedBy")}>
                  {approver ? (
                    <UserInfoBadge
                      avatarIcon={<Icon icon={mdiAccount} />}
                      avatarType="icon"
                      info={approver.full_name}
                      photoUser={approver}
                      subInfo={approver.login}
                    />
                  ) : null}
                </InfoField>
                <InfoField
                  label={t("common.approvedAt")}
                  text={t("intlDateTime", i18nextParseToDate(updatedAt))}
                />
              </Box>
            </AppCard>
          ) : null}
        </Grid>

        <Grid
          size={4}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <AppCard
            icon={<Icon icon={mdiListBoxOutline} />}
            title={t(
              "requestAnticipationEvaluationQuotations.titles.requestStatus",
            )}
          >
            <Chip
              color={getStatusColor(status)}
              label={t(`common.${status}`)}
              sx={{ width: "fit-content" }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <InfoField
                label={t("common.createdAt")}
                labelColor="text.secondary"
                labelIconPath={mdiCalendarBlank}
                text={t("intlDateTime", i18nextParseToDate(createdAt))}
                textColor="text.secondary"
              />
              {approver &&
              ["approved", "automatically_closed"].includes(status) ? (
                <InfoField
                  label={t("common.approvedAt")}
                  labelColor="success.main"
                  labelIconColor="success"
                  labelIconPath={mdiCheckCircleOutline}
                  text={t("intlDateTime", i18nextParseToDate(updatedAt))}
                  textColor="success.main"
                />
              ) : null}
              {approver && status === "rejected" ? (
                <InfoField
                  label={t("common.rejectedAt")}
                  labelColor="error.main"
                  labelIconColor="error"
                  labelIconPath={mdiCloseCircleOutline}
                  text={t("intlDateTime", i18nextParseToDate(updatedAt))}
                  textColor="error.main"
                />
              ) : null}
            </Box>
          </AppCard>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/request_anticipation_service_quotations/$requestAnticipationServiceQuotationId/",
)({
  beforeLoad: ({ context }) => {
    if (
      !context.auth.user?.roles_names.includes(
        "Request Anticipation Service Quotation - View",
      )
    ) {
      throw new ForbiddenError();
    }
  },
  component: DetailsPage,
});
